'use client';

import { useState } from 'react';
import { LineChart, Line, AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import Icon from '@/components/ui/AppIcon';

interface PopulationDataPoint {
  year: string;
  whiteRhino: number | null;
  blackRhino: number | null;
  total: number;
}

interface PopulationChartProps {
  data: PopulationDataPoint[];
}

const PopulationChart = ({ data }: PopulationChartProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [chartType, setChartType] = useState<'line' | 'area' | 'bar'>('line');

  useState(() => {
    setIsHydrated(true);
  });

  if (!isHydrated) {
    return (
      <div className="bg-card border border-border rounded-md p-6">
        <div className="h-8 w-48 bg-muted rounded-md animate-pulse mb-4"></div>
        <div className="h-80 bg-muted rounded-md animate-pulse"></div>
      </div>
    );
  }

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-card border border-border rounded-md p-4 shadow-organic">
          <p className="text-sm font-headline font-bold text-foreground mb-2">{label}</p>
          {payload.map((entry: any, index: number) => (
            <div key={index} className="flex items-center justify-between space-x-4 text-sm font-body">
              <span style={{ color: entry.color }}>{entry.name}:</span>
              <span className="font-semibold text-foreground">{entry.value.toLocaleString()}</span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  const renderChart = () => {
    const commonProps = {
      data,
      margin: { top: 10, right: 30, left: 0, bottom: 0 }
    };

    switch (chartType) {
      case 'area':
        return (
          <AreaChart {...commonProps}>
            <defs>
              <linearGradient id="colorWhite" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#8B4513" stopOpacity={0.8}/>
                <stop offset="95%" stopColor="#8B4513" stopOpacity={0}/>
              </linearGradient>
              <linearGradient id="colorBlack" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#D2691E" stopOpacity={0.8}/>
                <stop offset="95%" stopColor="#D2691E" stopOpacity={0}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(139, 69, 19, 0.1)" />
            <XAxis dataKey="year" stroke="#8B7355" style={{ fontSize: '12px', fontFamily: 'Source Sans 3' }} />
            <YAxis stroke="#8B7355" style={{ fontSize: '12px', fontFamily: 'Source Sans 3' }} />
            <Tooltip content={<CustomTooltip />} />
            <Legend wrapperStyle={{ fontSize: '14px', fontFamily: 'Source Sans 3' }} />
            <Area type="monotone" dataKey="whiteRhino" stroke="#8B4513" fillOpacity={1} fill="url(#colorWhite)" name="White Rhino" />
            <Area type="monotone" dataKey="blackRhino" stroke="#D2691E" fillOpacity={1} fill="url(#colorBlack)" name="Black Rhino" />
          </AreaChart>
        );
      case 'bar':
        return (
          <BarChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(139, 69, 19, 0.1)" />
            <XAxis dataKey="year" stroke="#8B7355" style={{ fontSize: '12px', fontFamily: 'Source Sans 3' }} />
            <YAxis stroke="#8B7355" style={{ fontSize: '12px', fontFamily: 'Source Sans 3' }} />
            <Tooltip content={<CustomTooltip />} />
            <Legend wrapperStyle={{ fontSize: '14px', fontFamily: 'Source Sans 3' }} />
            <Bar dataKey="whiteRhino" fill="#8B4513" name="White Rhino" />
            <Bar dataKey="blackRhino" fill="#D2691E" name="Black Rhino" />
          </BarChart>
        );
      default:
        return (
          <LineChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(139, 69, 19, 0.1)" />
            <XAxis dataKey="year" stroke="#8B7355" style={{ fontSize: '12px', fontFamily: 'Source Sans 3' }} />
            <YAxis stroke="#8B7355" style={{ fontSize: '12px', fontFamily: 'Source Sans 3' }} />
            <Tooltip content={<CustomTooltip />} />
            <Legend wrapperStyle={{ fontSize: '14px', fontFamily: 'Source Sans 3' }} />
            <Line type="monotone" dataKey="whiteRhino" stroke="#8B4513" strokeWidth={3} dot={{ fill: '#8B4513', r: 4 }} name="White Rhino" />
            <Line type="monotone" dataKey="blackRhino" stroke="#D2691E" strokeWidth={3} dot={{ fill: '#D2691E', r: 4 }} name="Black Rhino" />
          </LineChart>
        );
    }
  };

  return (
    <div className="bg-card border border-border rounded-md p-4 lg:p-6 shadow-organic-sm">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 space-y-4 sm:space-y-0">
        <h3 className="text-xl font-headline font-bold text-foreground">Population Trends</h3>
        
        <div className="flex items-center space-x-2 bg-muted rounded-md p-1">
          <button
            onClick={() => setChartType('line')}
            className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-body font-medium transition-organic duration-300 ${
              chartType === 'line' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon name="ChartBarIcon" size={16} variant={chartType === 'line' ? 'solid' : 'outline'} />
            <span className="hidden sm:inline">Line</span>
          </button>
          <button
            onClick={() => setChartType('area')}
            className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-body font-medium transition-organic duration-300 ${
              chartType === 'area' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon name="ChartPieIcon" size={16} variant={chartType === 'area' ? 'solid' : 'outline'} />
            <span className="hidden sm:inline">Area</span>
          </button>
          <button
            onClick={() => setChartType('bar')}
            className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-body font-medium transition-organic duration-300 ${
              chartType === 'bar' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon name="Squares2X2Icon" size={16} variant={chartType === 'bar' ? 'solid' : 'outline'} />
            <span className="hidden sm:inline">Bar</span>
          </button>
        </div>
      </div>

      <div className="w-full h-80" aria-label="Rhino Population Trends Chart">
        <ResponsiveContainer width="100%" height="100%">
          {renderChart()}
        </ResponsiveContainer>
      </div>

      <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-muted rounded-md p-4">
          <p className="text-sm font-body text-muted-foreground mb-1">Current Total</p>
          <p className="text-2xl font-headline font-bold text-primary">{data[data.length - 1].total.toLocaleString()}</p>
        </div>
        <div className="bg-muted rounded-md p-4">
          <p className="text-sm font-body text-muted-foreground mb-1">White Rhino</p>
          <p className="text-2xl font-headline font-bold text-foreground">{data[data.length - 1].whiteRhino?.toLocaleString()}</p>
        </div>
        <div className="bg-muted rounded-md p-4">
          <p className="text-sm font-body text-muted-foreground mb-1">Black Rhino</p>
          <p className="text-2xl font-headline font-bold text-foreground">{data[data.length - 1].blackRhino?.toLocaleString()}</p>
        </div>
      </div>
    </div>
  );
};

export default PopulationChart;